// Funnel v2 OTO — the FREE offer (issue #266).
//
// The buyer just paid $47–$74 for a plan. Their fear is that it becomes one
// more document that never turns into anything. This page closes that gap at
// zero price: the implementation course free, the trial armed for the
// activation click, 2,500 credits of agent work waiting — in exchange for a
// saved card and honest, visible billing terms.
//
// Copy register: calm operator. No exclamation points, no countdowns, no
// "audit", no refund language. The decline path is treated with respect —
// decliners converting later is the funnel's primary metric.

import { useSearchParams } from 'react-router-dom';
import { BadgeCheck, BookOpen, Rocket } from 'lucide-react';
import { OtoButtons } from '../../components/marketing/OtoButtons';
import { OTO_LEDGER_ROWS } from '../../lib/oto-ledger';
import { BillingTimeline } from '../../components/marketing/funnel/BillingTimeline';
import {
  DisplayHeading,
  Kicker,
  LedgerCard,
  LedgerRow,
  SectionMark,
} from '../../components/marketing/funnel/atoms';

const steps = [
  ['Add the upgrade below.', 'One click. It attaches to the card from the purchase you just made — nothing is charged, nothing to re-enter. Your trial waits until you choose to start it.'],
  ['Build your Blueprint.', 'The guided conversation maps the jobs she should hold, the skills each one uses, and the order to set them up in. Come back to it as often as you like.'],
  ['When it is ready, you choose.', 'Open the course and build by hand, or click Set it up in VibeControl and watch it deploy your plan with your trial and 2,500 credits already in place.'],
  ['Before we bill you, we email you.', 'Three days before your trial ends we send a reminder naming the amount and the date. Keep VibeControl by doing nothing, or cancel in one click. Either way the course, your Blueprint, and everything you built stay yours.'],
];

const faqs = [
  [
    'Will I be charged today?',
    'No. Nothing is charged at this step. The 14-day trial begins only when you click Set it up in VibeControl. After the trial ends, Standard is $97 per month unless you cancel first.',
  ],
  [
    'What if I never activate?',
    'Then no subscription is ever created and you are never billed. You keep the course and your Blueprint either way.',
  ],
  [
    'Do I have to enter my card again?',
    'No. The upgrade uses the card from the purchase you just completed. Nothing is charged today — it is simply what lets your trial start the moment you activate, with no extra checkout in the way.',
  ],
  [
    'Can I cancel during the trial?',
    'Yes — one click in your account settings, any time before day 14. You will not be charged for the Standard plan, and you keep the course, your Blueprint, and anything you built during the trial.',
  ],
  [
    'Can I get the course without the trial?',
    'Not as part of this upgrade. The course may be sold separately later at its own price; free-with-trial-consent exists only here, attached to your Blueprint purchase.',
  ],
  [
    'What if my Blueprint recommends jobs VibeControl cannot run?',
    'RoleForge and VibeControl are built as one system, so her jobs deploy as designed. If something unusual comes up, email us during your trial and we will work it out with you.',
  ],
];

export function OtoPage() {
  const [params] = useSearchParams();
  const paymentIntentId = params.get('payment_intent_id') ?? 'preview-payment-intent';

  return (
    <section className="px-5 py-10 md:px-8 md:py-14">
      <div className="mx-auto max-w-6xl">
        {/* Reassurance first: the purchase is secured. */}
        <div className="fv2-reveal flex items-center gap-2.5" style={{ '--fv2-i': 0 } as React.CSSProperties}>
          <BadgeCheck className="h-4 w-4 text-brand-teal" />
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand-teal/90">
            Payment received — your Blueprint is secured
          </p>
        </div>

        <div className="mt-6 grid gap-10 lg:grid-cols-[1fr_400px] lg:items-start">
          <div className="space-y-14">
            <header>
              <DisplayHeading className="fv2-reveal max-w-2xl text-4xl md:text-[56px]" >
                <span style={{ '--fv2-i': 1 } as React.CSSProperties} className="fv2-reveal block">
                  Your Blueprint is paid for.
                </span>
                <span style={{ '--fv2-i': 2 } as React.CSSProperties} className="fv2-reveal block text-slate-400">
                  Now make sure it gets built.
                </span>
              </DisplayHeading>
              <p
                style={{ '--fv2-i': 3 } as React.CSSProperties}
                className="fv2-reveal mt-6 max-w-2xl text-lg leading-8 text-slate-300"
              >
                The owners who get the most out of RoleForge decide one thing before their first
                session: how the plan becomes a working AI employee. There is a free upgrade on this
                page that covers both ways of doing that. It exists only here, attached to the
                purchase you just made.
              </p>
            </header>

            {/* The trust device: when would you ever be billed. */}
            <div
              style={{ '--fv2-i': 4 } as React.CSSProperties}
              className="fv2-reveal rounded-lg border border-white/10 bg-[#0b0f16]/80 p-6 md:p-8"
            >
              <Kicker className="mb-8">When you would ever be billed</Kicker>
              <BillingTimeline />
            </div>

            <div>
              <SectionMark no="01" label="What the upgrade covers" />
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-6">
                  <BookOpen className="mb-4 h-5 w-5 text-brand-teal" />
                  <h2 className="font-display text-xl font-semibold text-white">
                    The implementation course, free
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-slate-400">
                    A working course on turning your Blueprint into a working AI employee by hand,
                    with the AI tools you already use. Concepts, not button-clicking — it stays
                    useful no matter what your stack looks like. Yours to keep whether or not you
                    ever touch VibeControl.
                  </p>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-6">
                  <Rocket className="mb-4 h-5 w-5 text-brand-coral" />
                  <h2 className="font-display text-xl font-semibold text-white">
                    The one-click path, ready
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-slate-400">
                    When your Blueprint is complete, one click deploys it in VibeControl: her jobs
                    set up, your 14-day Standard trial running, and 2,500 credits of platform-hosted
                    work included — enough to see her produce real output in week one.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <SectionMark no="02" label="Why it costs nothing" />
              <div className="max-w-2xl space-y-5 text-base leading-8 text-slate-300">
                <p>
                  We are not bundling this because we are feeling generous. Owners who see their
                  own Blueprint running — her jobs, your workflows, your voice — tend to
                  keep VibeControl. The trial is how you find that out on your real business,
                  and the course means that even if you walk away, you walk away equipped.
                </p>
                <p className="font-semibold text-white">
                  Skip it and nothing bad happens. You will just be implementing by hand, without
                  the course, and the trial will be the standard offer when you come looking for
                  it later.
                </p>
              </div>
            </div>

            <div>
              <SectionMark no="03" label="What happens next" />
              <ol className="grid gap-3">
                {steps.map(([title, body], index) => (
                  <li
                    key={title}
                    className="grid gap-4 rounded-md border border-white/10 bg-white/[0.03] p-5 md:grid-cols-[44px_1fr]"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 bg-[#0b0f16] font-mono text-sm font-semibold text-brand-teal">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <p className="leading-7 text-slate-300">
                      <strong className="text-white">{title}</strong> {body}
                    </p>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <SectionMark no="04" label="Asked before deciding" />
              <div className="divide-y divide-white/10 rounded-lg border border-white/10">
                {faqs.map(([q, a]) => (
                  <details key={q} className="group bg-white/[0.02] p-5 open:bg-white/[0.045]">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-white">
                      {q}
                      <span className="font-mono text-brand-coral transition group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-400">{a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          {/* The decision card — sticky, ledger-set, one coral action. */}
          <aside className="order-first lg:sticky lg:top-10 lg:order-none">
            <LedgerCard className="fv2-reveal" >
              <Kicker>Free upgrade — attached to this purchase</Kicker>
              <h2 className="mt-3 font-display text-[28px] font-semibold leading-tight tracking-tight text-white">
                Leave today with both paths covered.
              </h2>

              <div className="mt-5">
                {OTO_LEDGER_ROWS.map((row) => (
                  <LedgerRow key={row.label} label={row.label} sub={row.sub} amount={row.amount} />
                ))}
              </div>

              <div className="flex items-baseline justify-between pt-4 pb-5">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                  Due today
                </span>
                <span className="font-display text-3xl font-semibold tabular-nums text-white">$0</span>
              </div>

              <OtoButtons paymentIntentId={paymentIntentId} />
            </LedgerCard>
          </aside>
        </div>
      </div>
    </section>
  );
}
